import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';

import { LoadingService } from './loading.service';

/**
 * This is a list of routes that will not show the loading spinner.
 *
 * Define in this list the requests (URLs) that should have global
 * loading disabled within a route (page).
 * ! ATTENTION: Only include in this list requests that need to define manual
 * ! loading and that are complex to implement {args.loading : of};
 */
const SKIP_LOADING_URLS_BY_ROUTE_PATH = [
  {
    route: '/request/solicitation',
    request: [
      '/listeners/ms/dformsrd/requests/comercializadores/export',
      '/listeners/ms/dformsrd/export-tasks/get-exported-file',
    ],
  },
];

@Injectable()
export class GlobalInterceptor implements HttpInterceptor {
  private readonly loadingService = inject(LoadingService);

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const args = req.body?.args ?? false;

    if (req.url.includes('listeners')) {
      // the auth session of the user is checked at body.args.userAccount when is running in localhost (simplesaml don't look here)
      // so, this needs to be defined to pass validation through api.php, ms.php, etc.
      const currentUser = sessionStorage.getItem('currentUser');
      if (currentUser && !req.body?.args?.userAccount) {
        const newArgs = {
          userAccount: JSON.parse(currentUser).userAccount,
          supplierId: JSON.parse(currentUser).retailerSupplierId,
        };
        req.body.args ??= {};
        Object.assign(req.body.args, newArgs);
      }
    }

    if (this.shouldLoad(args, req.url)) {
      this.loadingService.onStarted(req);
    }

    return next.handle(req).pipe(
      tap({
        next: (event: HttpEvent<any>) => {},
        error: (err: any) => {
          this.handleError(err);
        },
      }),
      finalize(() => {
        if (this.shouldLoad(args, req.url)) {
          this.loadingService.onFinished(req);
        }
      })
    );
  }

  /**
   * If the error is a 401, and the URL doesn't include /listeners/user, then log out the user and
   * redirect them to the 401 page
   * @param  {any} err - any - The error object
   */
  handleError(err: any): void {
    if (err instanceof HttpErrorResponse) {
      if (err.status === 401 && !err.url.includes('/listeners/user')) {
        console.error('Authentication required!');
        sessionStorage.removeItem('currentUser');
        window.location.href = '/401';
      } else if (err.status === 403) {
        console.error('Permission denied!');
      }
    } else if (err.error instanceof Error) {
      console.error('An error occurred:', err.error.message);
    } else {
      console.error(`Backend returned code ${err.status}, body was: ${err.error}`);
    }
  }

  /**
   * If the URL is not in the list of URLs to skip loading, and the loading argument is not set to "off",
   * then load the URL
   * @param  {any} args - any - this is the args object that is passed to the component.
   * @param  {string} url - The url of the request
   * @returns {boolean} A boolean value.
   */
  private shouldLoad(args: any, url: string): boolean {
    const skipLoadingByRoute = SKIP_LOADING_URLS_BY_ROUTE_PATH.find(({ route }) => route === window.location.pathname);

    const shouldSkipLoading = skipLoadingByRoute ? skipLoadingByRoute.request.includes(url) : false;

    const shouldLoadArgs = !args?.loading || args.loading !== 'off';

    return !shouldSkipLoading && shouldLoadArgs;
  }
}
